import { HighlightCanvas } from '../HighlightCanvas';
import { BoundingBoxSelector, SelectionBox } from './BoundingBoxSelector';
import { DocViewerLoader } from './DocViewerLoader';
import { useGetDocImagePresignedURL } from '@/api/queries/useGetDocImagePresignedURL';
import { BoundingBox, BoundingBoxWithId } from '@/types';

interface ImagePageProps {
  pageNumber: number;
  top: number;
  dimensions: {
    height: number;
    width: number;
  };
  highlights: BoundingBoxWithId[];
  highlightedFactId?: string | null;
  onHighlightClick?: (id: string) => void;
  highlightedChunkId?: string | null;
  caseId: string;
  docId: string;
  isSelectionMode?: boolean;
  onSelectionComplete?: (
    boundingBox: BoundingBox,
    imageData: string,
    selectionBox: SelectionBox,
    pageNumber: number,
  ) => void;
  maintainSelection?: boolean;
  activeSelection?: { box: SelectionBox; pageNumber: number } | null;
}

export const ImagePage = ({
  pageNumber,
  top,
  dimensions,
  highlights,
  highlightedFactId,
  onHighlightClick,
  highlightedChunkId,
  caseId,
  docId,
  isSelectionMode,
  onSelectionComplete,
  maintainSelection,
  activeSelection,
}: ImagePageProps) => {
  const { data: imageUrl } = useGetDocImagePresignedURL(caseId, docId, pageNumber - 1);

  return (
    <div
      key={pageNumber}
      className="absolute overflow-hidden shadow-lg"
      style={{
        top: top,
        left: '50%',
        height: dimensions.height,
        width: dimensions.width,
        transform: 'translateX(-50%)',
      }}
      data-page-number={pageNumber}
    >
      {imageUrl ? (
        <>
          <img src={imageUrl} alt={`PDF Page ${pageNumber}`} className="select-none" crossOrigin="anonymous" />

          <HighlightCanvas
            highlights={highlights}
            pageNumber={pageNumber}
            width={dimensions.width}
            height={dimensions.height}
            highlightedFactId={highlightedFactId}
            onHighlightClick={onHighlightClick}
            highlightedChunkId={highlightedChunkId}
          />

          {(isSelectionMode || (maintainSelection && activeSelection?.pageNumber === pageNumber)) &&
            onSelectionComplete && (
              <BoundingBoxSelector
                isActive={
                  (isSelectionMode || (maintainSelection && activeSelection?.pageNumber === pageNumber)) ?? false
                }
                pageNumber={pageNumber}
                pageWidth={dimensions.width}
                pageHeight={dimensions.height}
                onSelectionComplete={(boundingBox, imageData, selectionBox) =>
                  onSelectionComplete(boundingBox, imageData, selectionBox, pageNumber)
                }
                maintainSelection={maintainSelection}
                activeSelection={activeSelection?.pageNumber === pageNumber ? activeSelection.box : null}
              />
            )}
        </>
      ) : (
        <DocViewerLoader />
      )}
    </div>
  );
};
